/**
 * Add Business, from the business switcher.
 *
 * The same `POST /businesses` onboarding uses, asking only the five things a
 * business cannot sensibly start without. Everything else onboarding asks —
 * phone, what it sells, where it sells, the first branch's name — has a
 * default the API applies and is editable in Settings afterwards.
 *
 * The new business is its own workspace: the API creates it with the caller as
 * its owner and nothing shared with any other business. On success the
 * switcher's list is re-read and the new business is selected, without
 * signing out.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  COUNTRIES,
  CURRENCIES,
  findCountry,
  onboardingChoices,
  statesOf,
  type Business,
} from '@cenox/shared';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Input, Modal, Select } from './ui';

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }));
const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }));
const CHOICES = onboardingChoices();

export function AddBusinessDialog({ open, onClose }: { open: boolean; onClose: () => void }): ReactNode {
  const { reload, selectBusiness } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('NG');
  const [stateCode, setStateCode] = useState('');
  const [choice, setChoice] = useState<string>(CHOICES[0]!.value);
  const [currencyCode, setCurrencyCode] = useState('NGN');
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const states = statesOf(countryCode);
  const selected = CHOICES.find((c) => c.value === choice) ?? CHOICES[0]!;

  // A fresh form each time it opens: the last attempt's values are not this one's.
  useEffect(() => {
    if (!open) return;
    setName('');
    setCountryCode('NG');
    setStateCode('');
    setChoice(CHOICES[0]!.value);
    setCurrencyCode('NGN');
    setCurrencyTouched(false);
    setError(null);
    setFieldErrors({});
  }, [open]);

  // The country suggests the currency until the owner picks one themselves.
  useEffect(() => {
    if (currencyTouched) return;
    const country = findCountry(countryCode);
    if (country) setCurrencyCode(country.currencyCode);
  }, [countryCode, currencyTouched]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!name.trim()) {
      setFieldErrors({ name: 'Enter the business name' });
      return;
    }
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      const created = await api.post<Business>('/businesses', {
        name: name.trim(),
        type: selected.type,
        presetSlug: selected.presetSlug,
        countryCode,
        stateCode: stateCode || undefined,
        currencyCode,
      });
      // Re-read the memberships first, so the switcher lists the new business
      // by the time it is selected.
      await reload();
      selectBusiness(created.id);
      onClose();
      navigate('/business');
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? ''])),
          );
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={() => !submitting && onClose()} title="Add business">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate aria-label="Add business">
        <Input
          label="Business name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fieldErrors.name}
          required
          autoFocus
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Country"
            options={COUNTRY_OPTIONS}
            value={countryCode}
            onChange={(e) => {
              // A state belongs to its country; another country's codes mean nothing here.
              setCountryCode(e.target.value);
              setStateCode('');
            }}
            error={fieldErrors.countryCode}
          />
          <Select
            label="State/region"
            options={states.map((s) => ({ value: s.code, label: s.name }))}
            placeholder={states.length ? 'Choose a state (optional)' : 'Not listed for this country'}
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value)}
            disabled={states.length === 0}
            error={fieldErrors.stateCode}
          />
        </div>
        <Select
          label="Business type"
          options={CHOICES.map((c) => ({ value: c.value, label: c.label }))}
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          hint={`${selected.hint} This can’t be changed after the business is created.`}
          error={fieldErrors.type}
        />
        <Select
          label="Currency"
          options={CURRENCY_OPTIONS}
          value={currencyCode}
          onChange={(e) => {
            setCurrencyTouched(true);
            setCurrencyCode(e.target.value);
          }}
          error={fieldErrors.currencyCode}
          hint="Every figure in this business is recorded in this currency"
        />
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create business'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
